import { computed, ComputedRef, ref } from "vue";
import { ITableHeader } from "@/utils";
import { IColumnFilter, IFilter } from "@/types";
import { CreatedColumns } from "@/services/columns/columns";
import { IListItem } from "@/components-UI/list/types";
import { EventTrace } from "@/api/types/event-tracer";
import { CreatedFilters } from "@/services/filters/filters";
import { filterClass } from "@/services/filters/filterClass";
import { getAdvertisersSearch } from "@/api/revenue";
import { getActiveUserSearch, getAffiliateSearch } from "@/api/user";
import { getEmployeesOffersV2 } from "@/api/offers";
import { affiliateStatuses } from "@/mappings/statuses";
import {
    translateEventType,
    translateOffersStatus,
    translatePartnerStatus,
    translatePayoutMethodStatus,
} from "@/utils/translate";
import { IUser } from "@/api/types/user";
import { getCurrentDate } from "@/utils/formatDates";

export const useEventTracer = (): { getHeaders: ComputedRef<ITableHeader[]> } => {
    const headers = ref<ITableHeader[]>([
        { section: 'Событие', text: 'ID события', value: 'event_id', align: 'center', width: '100px' },
        { section: 'Событие', text: 'Тип события', value: 'event_type', width: '100px' },
        { section: 'Событие', text: 'Дата события', value: 'event_date', width: '100px' },
        { section: 'Идентификаторы', text: 'Transaction ID', value: 'transaction_id' },
        { section: 'Идентификаторы', text: 'Click ID', value: 'click_id' },
        { section: 'Идентификаторы', text: 'User ID', value: 'user_id' },
        { section: 'Запрос', text: 'Метод запроса', value: 'request_method' },
        { section: 'Запрос', text: 'URL запроса', value: 'request_url', width: '400px' },
        { section: 'Запрос', text: 'IP клиента', value: 'client_ip' },
        { section: 'Запрос', text: 'User agent', value: 'user_agent', width: '400px' },
        { section: 'Запрос', text: 'Бот', value: 'is_bot', width: '70px' },
        { section: 'Запрос', text: 'Referrer URL', value: 'referer' },
        { section: 'Запрос', text: 'Валидность', value: 'is_valid' },
        { section: 'Запрос', text: 'Причина невалидности', value: 'invalid_reason' },
        { section: 'Ответ', text: 'Код ответа', value: 'response_code', width: '70px' },
        { section: 'Ответ', text: 'Redirect', value: 'response_location', width: '500px' },
        { section: 'Ответ', text: 'Backurl', value: 'is_backurl' },
        { section: 'Ответ', text: 'Backurl reason', value: 'backurl_reason', width: '100px' },
        { section: 'Рекламодатель', text: 'ID рекламодателя', value: 'advertiser_id', align: 'center' },
        { section: 'Рекламодатель', text: 'Рекламодатель', value: 'advertiser_name' },
        {
            section: 'Рекламодатель',
            text: 'ID менеджера рекламодателя',
            value: 'advertiser_manager_id',
            width: '90px',
            lineBreak: true,
        },
        { section: 'Рекламодатель', text: 'Менеджер рекламодателя', value: 'advertiser_manager_name', lineBreak: true },
        { section: 'Рекламодатель', text: 'Sub ID рекламодателя', value: 'adv_sub' },
        { section: 'Оффер', text: 'ID оффера', value: 'offer_id', align: 'center', width: '100px' },
        { section: 'Оффер', text: 'Оффер', value: 'offer_name' },
        { section: 'Оффер', text: 'ID цели', value: 'offer_goal_id', align: 'center', width: '100px' },
        { section: 'Оффер', text: 'Цель', value: 'offer_goal_name' },
        { section: 'Оффер', text: 'Статус оффера', value: 'offer_status', align: 'center' },
        { section: 'Оффер', text: 'Валюта', value: 'offer_currency' },
        { section: 'Оффер', text: 'URL оффера', value: 'offer_url_id', align: 'center', width: '100px' },
        { section: 'Оффер', text: 'Имя URL', value: 'offer_url_name' },
        { section: 'Оффер', text: 'Статус URL', value: 'offer_url_status', align: 'center' },
        { section: 'Партнёр', text: 'ID мастера', value: 'master_id', align: 'center' },
        { section: 'Партнёр', text: 'Мастер', value: 'master_name' },
        { section: 'Партнёр', text: 'ID менеджера мастера', value: 'master_manager_id', width: '90px', lineBreak: true },
        { section: 'Партнёр', text: 'Менеджер мастера', value: 'master_manager_name' },
        { section: 'Партнёр', text: 'Буфер', value: 'buffer_account_used' },
        { section: 'Партнёр', text: 'ID буфера', value: 'buffer_account_id', align: 'center', width: '100px' },
        { section: 'Партнёр', text: 'Имя буфера', value: 'buffer_account_name' },
        { section: 'Партнёр', text: 'ID партнёра', value: 'affiliate_id', align: 'center', width: '100px' },
        { section: 'Партнёр', text: 'Партнёр', value: 'affiliate_name' },
        { section: 'Партнёр', text: 'Статус партнёра', value: 'affiliate_status', align: 'center' },
        {
            section: 'Партнёр',
            text: 'ID менеджера партнёра',
            value: 'affiliate_manager_id',
            width: '90px',
            lineBreak: true,
        },
        { section: 'Партнёр', text: 'Менеджер партнёра', value: 'affiliate_manager_name' },
        { section: 'Партнёр', text: 'aff_sub', value: 'aff_sub' },
        { section: 'Erir', text: 'Erir использован', value: 'erir_token_used' },
        { section: 'Erir', text: 'Токен Erir', value: 'erir_token' },
        {
            section: 'Erir',
            text: 'Платформа Erir использована',
            value: 'erir_platform_used',
            width: '100px',
            lineBreak: true,
        },
        { section: 'Erir', text: 'ID платформы Erir', value: 'erir_platform_id' },
    ]);

    const getHeaders = computed(() => {
        const subs: ITableHeader[] = [];
        for (let i = 1; i <= 25; i++) {
            subs.push({ section: 'Sub ID партнёра', text: `Sub ID ${i}`, value: `aff_sub${i}` });
        }
        const templateIndex = headers.value.findIndex((h) => h.value === 'aff_sub');
        headers.value.splice(templateIndex, 1, ...subs);
        return headers.value;
    });

    return { getHeaders };
};

export class EventTracerColumns extends CreatedColumns {
    constructor() {
        super();
    }

    getColumns(): IColumnFilter[] {
        return [
            {
                name: 'Событие',
                items: [
                    { title: 'Тип события', value: 'event_type', default: true },
                    { title: 'Дата события', value: 'event_date', default: true },
                ],
            },
            {
                name: 'Идентификаторы',
                items: [
                    { title: 'Transaction ID', value: 'transaction_id', default: true },
                    { title: 'Click ID', value: 'click_id' },
                    { title: 'User ID', value: 'user_id' },
                ],
            },
            {
                name: 'Запрос',
                items: [
                    { title: 'Метод запроса', value: 'request_method' },
                    { title: 'URL запроса', value: 'request_url', default: true },
                    { title: 'IP клиента', value: 'client_ip' },
                    { title: 'User-Agent', value: 'user_agent' },
                    { title: 'Бот', value: 'is_bot' },
                    { title: 'Referrer URL', value: 'referer' },
                    { title: 'Валидность', value: 'is_valid' },
                    { title: 'Причина невалидности', value: 'invalid_reason' },
                ],
            },
            {
                name: 'Ответ',
                items: [
                    { title: 'Код ответа', value: 'response_code', default: true },
                    { title: 'Redirect', value: 'response_location' },
                    { title: 'Backurl', value: 'is_backurl' },
                    { title: 'Backurl reason', value: 'backurl_reason' },
                ],
            },
            {
                name: 'Рекламодатель',
                items: [
                    { title: 'ID рекламодателя', value: 'advertiser_id' },
                    { title: 'Рекламодатель', value: 'advertiser_name' },
                    { title: 'ID менеджера рекламодателя', value: 'advertiser_manager_id' },
                    { title: 'Менеджер рекламодателя', value: 'advertiser_manager_name' },
                    { title: 'Sub ID рекламодателя', value: 'adv_sub' },
                ],
            },
            {
                name: 'Оффер',
                items: [
                    { title: 'ID оффера', value: 'offer_id', default: true },
                    { title: 'Оффер', value: 'offer_name', default: true },
                    { title: 'ID цели', value: 'offer_goal_id' },
                    { title: 'Цель', value: 'offer_goal_name' },
                    { title: 'Статус оффера', value: 'offer_status' },
                    { title: 'Валюта', value: 'offer_currency' },
                    { title: 'URL оффера', value: 'offer_url_id' },
                    { title: 'Имя URL', value: 'offer_url_name' },
                    { title: 'Статус URL', value: 'offer_url_status' },
                ],
            },
            {
                name: 'Партнёр',
                items: [
                    { title: 'ID мастера', value: 'master_id' },
                    { title: 'Имя мастера', value: 'master_name' },
                    { title: 'ID менеджера мастера', value: 'master_manager_id' },
                    { title: 'Менеджер мастера', value: 'master_manager_name' },
                    { title: 'Буфер', value: 'buffer_account_used' },
                    { title: 'ID буфера', value: 'buffer_account_id' },
                    { title: 'Имя буфера', value: 'buffer_account_name' },
                    { title: 'ID партнёра', value: 'affiliate_id', default: true },
                    { title: 'Партнёр', value: 'affiliate_name', default: true },
                    { title: 'Статус партнёра', value: 'affiliate_status' },
                    { title: 'ID менеджера партнёра', value: 'affiliate_manager_id' },
                    { title: 'Менеджер партнёра', value: 'affiliate_manager_name' },
                ],
            },
            {
                name: 'Sub ID',
                isScrollable: true,
                items: [
                    { title: 'Sub ID 1', value: 'aff_sub1' },
                    { title: 'Sub ID 2', value: 'aff_sub2' },
                    { title: 'Sub ID 3', value: 'aff_sub3' },
                    { title: 'Sub ID 4', value: 'aff_sub4' },
                    { title: 'Sub ID 5', value: 'aff_sub5' },
                    { title: 'Sub ID 6', value: 'aff_sub6' },
                    { title: 'Sub ID 7', value: 'aff_sub7' },
                    { title: 'Sub ID 8', value: 'aff_sub8' },
                    { title: 'Sub ID 9', value: 'aff_sub9' },
                    { title: 'Sub ID 10', value: 'aff_sub10' },
                    { title: 'Sub ID 11', value: 'aff_sub11' },
                    { title: 'Sub ID 12', value: 'aff_sub12' },
                    { title: 'Sub ID 13', value: 'aff_sub13' },
                    { title: 'Sub ID 14', value: 'aff_sub14' },
                    { title: 'Sub ID 15', value: 'aff_sub15' },
                    { title: 'Sub ID 16', value: 'aff_sub16' },
                    { title: 'Sub ID 17', value: 'aff_sub17' },
                    { title: 'Sub ID 18', value: 'aff_sub18' },
                    { title: 'Sub ID 19', value: 'aff_sub19' },
                    { title: 'Sub ID 20', value: 'aff_sub20' },
                    { title: 'Sub ID 21', value: 'aff_sub21' },
                    { title: 'Sub ID 22', value: 'aff_sub22' },
                    { title: 'Sub ID 23', value: 'aff_sub23' },
                    { title: 'Sub ID 24', value: 'aff_sub24' },
                    { title: 'Sub ID 25', value: 'aff_sub25' },
                ],
            },
            {
                name: 'Erir',
                items: [
                    { title: 'Erir использован', value: 'erir_token_used' },
                    { title: 'Токен Erir', value: 'erir_token' },
                    { title: 'Платформа Erir использована', value: 'erir_platform_used' },
                    { title: 'ID платформы Erir', value: 'erir_platform_id' },
                ],
            },
        ];
    }
}

export class EventTraceFilters extends CreatedFilters {
    constructor() {
        super();
        this.addSubsToFilters();
    }

    getFilters(): IFilter[] {
        const today = getCurrentDate('yyyy-MM-dd');
        return [
            new filterClass.ComboboxArray({
                col: 1,
                id: 'event_id',
                label: 'ID события',
                select: [],
                type: 'number',
                operator: 'in',
                multiPaste: true,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'event_type',
                label: 'Тип события',
                itemValue: 'id',
                clearable: true,
                operator: 'in',
                items: [{ id: 'click' }, { id: 'postback' }, { id: 'pixel' }, { id: 'zip_link' }, { id: 'redirect' }, { id: 'root_redirect' }],
                // select: ['click', 'postback', 'pixel'],
                defaultValue: ['click', 'postback', 'pixel'],
                template: ({ id }) => translateEventType(id),
                itemText: ({ id }) => translateEventType(id),
            }),
            new filterClass.Datepicker({
                col: 1,
                id: 'event_date',
                label: 'Период',
                pickerType: 'date',
                select: [],
                defaultValue: [today, today],
                range: true,
                operator: 'between',
                clearable: true,
                isHidden: true,
                isOnlyPlaceholder: true,
            }),
            new filterClass.ComboboxArray({
                col: 1,
                id: 'transaction_id',
                label: 'ID transaction',
                select: [],
                type: 'string',
                operator: 'in',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 1,
                id: 'click_id',
                label: 'ID клика',
                select: [],
                type: 'string',
                operator: 'in',
                multiPaste: true,
            }),
            new filterClass.Select({
                col: 1,
                id: 'is_bot',
                label: 'Бот',
                itemValue: 'value',
                itemText: 'text',
                select: false,
                defaultValue: 'false',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Input({
                col: 1,
                id: 'request_url',
                label: 'URL запроса',
                select: '',
                operator: 'like',
            }),
            new filterClass.Input({
                col: 1,
                id: 'user_agent',
                label: 'User Agent',
                select: '',
                operator: 'like',
            }),
            new filterClass.Input({
                col: 1,
                id: 'referer',
                label: 'Referer URL',
                select: '',
                operator: 'like',
            }),
            new filterClass.Input({
                col: 1,
                id: 'response_location',
                label: 'Redirect',
                select: '',
                operator: 'like',
            }),
            new filterClass.Select({
                col: 1,
                id: 'is_valid',
                label: 'Валидность',
                itemValue: 'value',
                itemText: 'text',
                select: true,
                defaultValue: 'true',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Select({
                col: 1,
                id: 'is_backurl',
                label: 'Backurl',
                itemValue: 'value',
                itemText: 'text',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'advertiser_id',
                apiMethod: getAdvertisersSearch,
                label: 'Рекламодатель',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'advertiser_manager_id',
                apiMethod: getActiveUserSearch,
                key: 'search',
                label: 'Менеджер рекламодателя',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
                pinCurrentUser: true,
            }),
            new filterClass.ComboboxArray({
                col: 2,
                id: 'adv_sub',
                label: 'Sub ID рекламодателя',
                select: [],
                type: 'string',
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'offer_currency',
                label: 'Валюта',
                noDataText: 'Нет выбранной валюты',
                select: [],
                items: [],
                itemValue: 'code',
                itemText: 'code',
                type: 'string',
                isCurrencyCatalogue: true,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'offer_id',
                apiMethod: getEmployeesOffersV2,
                key: 'search',
                label: 'Оффер',
                itemValue: 'legacy_id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'offer_goal_id',
                itemValue: 'legacy_id',
                itemText: (item: any): string => `${item.legacy_id} ${item.name}`,
                label: 'Цель',
                noDataText: 'Выберите оффер',
                clearable: true,
                disabled: true,
                type: 'number',
                template: (item: any): string => `${item.legacy_id} ${item.name}`,
                tooltip: 'Сначала отфильтруйте отчёт по офферу',
                operator: 'in',
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'offer_url_id',
                itemValue: 'legacy_id',
                itemText: (item: any): string => `${item.legacy_id} ${item.name}`,
                label: 'URL оффера',
                noDataText: 'Выберите оффер',
                clearable: true,
                disabled: true,
                type: 'number',
                template: (item: any): string => `${item.legacy_id} ${item.name}`,
                tooltip: 'Сначала отфильтруйте отчёт по офферу',
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 3,
                id: 'affiliate_id',
                apiMethod: getAffiliateSearch,
                label: 'Партнёр',
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
                select: [],
                items: [],
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Select({
                col: 3,
                id: 'affiliate_status',
                label: 'Статус',
                items: affiliateStatuses,
                select: '',
                clearable: true,
                itemValue: 'value',
                itemText: 'name',
                translate: (i) => translatePartnerStatus(i).text,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 3,
                id: 'affiliate_manager_id',
                apiMethod: getActiveUserSearch,
                key: 'search',
                label: 'Менеджер партнёра',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
                pinCurrentUser: true,
            }),
            new filterClass.Autocomplete({
                col: 3,
                id: 'master_id',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                itemText: 'text',
                label: 'Мастер-аккаунт',
                template: (item: IUser): string => `${item.id!}`,
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 3,
                id: 'master_manager_id',
                apiMethod: getActiveUserSearch,
                key: 'search',
                label: 'Менеджер мастера',
                itemValue: 'id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Autocomplete({
                col: 3,
                id: 'buffer_account_id',
                apiMethod: getAffiliateSearch,
                label: 'ID буфера',
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                operator: 'in',
            }),
            new filterClass.Select({
                col: 3,
                id: 'buffer_account_used',
                label: 'Буфер',
                itemValue: 'value',
                itemText: 'text',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Base({
                id: 'template',
                label: 'Заглушка',
            }),
            new filterClass.Select({
                col: 5,
                id: 'erir_token_used',
                label: 'Erir использован',
                itemValue: 'value',
                itemText: 'text',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Input({
                col: 5,
                id: 'erir_token',
                label: 'Токен Erir',
                select: '',
                operator: 'in',
            }),
            new filterClass.Select({
                col: 5,
                id: 'erir_platform_used',
                label: 'Платформа Erir использована',
                itemValue: 'value',
                itemText: 'text',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
                operator: 'eq',
            }),
            new filterClass.Input({
                col: 5,
                id: 'erir_platform_id',
                label: 'ID платформы Erir',
                select: '',
                operator: 'in',
                type: 'number',
            }),
        ];
    }

    addSubsToFilters(): void {
        const subs: IFilter[] = [];

        for (let i = 1; i <= 25; i++) {
            subs.push(
                new filterClass.Base({
                    col: 4,
                    colName: 'Sub ID',
                    id: `aff_sub${i}`,
                    label: `Sub ID ${i}`,
                    type: 'string',
                    select: [],
                    customSlot: true,
                    multiple: true,
                    isSubmitEmptySelect: false,
                    isExcluded: false,
                    operator: 'in',
                }),
            );
        }

        const templateFilter = this.filters.find((filter) => filter.id === 'template')!;
        this.filters.splice(this.filters.indexOf(templateFilter), 1, ...subs);
    }

    prepareFiltersForSend(selectedFilters: { [key: string]: any }): any[] {
        const filters: {
            column: string;
            operator: string;
            values: any;
        }[] = [];
        for (const key in selectedFilters) {
            if (key === 'event_date_to') continue;
            if (key === 'event_date_from') {
                const filter = this.filters.find((filter) => filter.id === 'event_date')!;
                if (!filter) continue;
                const item = {
                    column: filter.id,
                    operator: filter.operator!,
                    values: [selectedFilters['event_date_from'], selectedFilters['event_date_to']],
                };
                filters.push(item);
            } else {
                const filter = this.filters.find((filter) => filter.id === key)!;
                if (!filter) continue;
                const value = selectedFilters[key];
                const values = Array.isArray(value) ? value : [value];
                const item = {
                    column: filter.id,
                    operator: filter.operator!,
                    values,
                };
                filters.push(item);
            }
        }
        return filters;
    }
}

type EventItem = IListItem<EventTrace>;
type EventCardName = 'offer' | 'affiliate' | 'subIds' | 'erir' | 'event' | 'request' | 'response';
export type EventCards = {
    [key in EventCardName]: {
        title: string;
        items: EventItem[];
        col: number;
    }
}

export const useEventTraceSingle = (): { getCards: (data: EventTrace) => EventCards; getFields: () => (keyof EventTrace)[] } => {
    const offer: EventItem[] = [
        {
            itemType: 'route',
            label: 'Рекламодатель',
            routeName: 'advertiser',
            routeText: 'advertiser_id',
            additionalText: 'advertiser_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'advertiser_id',
        },
        {
            itemType: 'text',
            label: 'Менеджер',
            value: '',
            valueKey: 'advertiser_manager_name',
        },
        {
            itemType: 'route',
            label: 'Оффер',
            routeName: 'offer',
            routeText: 'offer_id',
            additionalText: 'offer_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'offer_id',
            status: (i: EventTrace) => translateOffersStatus(i.offer_status),
        },
        {
            itemType: 'route',
            label: 'Цель',
            routeName: 'offer',
            routeText: 'offer_goal_id',
            additionalText: 'offer_goal_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'offer_goal_id',
        },
        {
            itemType: 'route',
            label: 'URL оффера',
            routeName: 'offer',
            routeText: 'offer_url_id',
            additionalText: 'offer_url_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'offer_id',
            status: (i: EventTrace) => translatePayoutMethodStatus(i.offer_url_status),
        },
        {
            itemType: 'text',
            label: 'Валюта',
            value: '',
            valueKey: 'offer_currency',
        },
        {
            itemType: 'code',
            label: 'Sub ID рекл.',
            value: '',
            isCopyOnClick: true,
            isFullWidthValue: true,
            valueKey: 'adv_sub',
        },
    ];

    const affiliate: EventItem[] = [
        {
            itemType: 'route',
            label: 'Мастер-аккаунт',
            routeName: 'affiliate',
            routeText: 'master_id',
            additionalText: 'master_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'master_id',
        },
        {
            itemType: 'text',
            label: 'Менеджер мастера',
            value: '',
            valueKey: 'master_manager_name',
        },
        {
            itemType: 'route',
            label: 'Имя буфера',
            routeName: 'affiliate',
            routeText: 'buffer_account_id',
            additionalText: 'buffer_account_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'buffer_account_id',
        },
        {
            itemType: 'route',
            label: 'Партнёр',
            routeName: 'affiliate',
            routeText: 'affiliate_id',
            additionalText: 'affiliate_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'affiliate_id',
            status: (i: EventTrace) => translatePartnerStatus(i.affiliate_status),
        },
        {
            itemType: 'text',
            label: 'Менеджер партнёра',
            value: '',
            valueKey: 'affiliate_manager_name',
        },
    ];

    const subIds: EventItem[] = Array.from({ length: 25 }, (_, index) => ({
        itemType: 'code',
        label: `Sub ID ${index + 1}`,
        isCopyOnClick: true,
        isFullWidthValue: true,
        value: '',
        valueKey: `aff_sub${index + 1}`,
    }));

    const erir: EventItem[] = [
        {
            itemType: 'code',
            label: 'Токен Erir',
            isCopyOnClick: true,
            isFullWidthValue: true,
            value: '',
            valueKey: `erir_token`,
        },
        {
            itemType: 'code',
            label: `ID платформы Erir`,
            isCopyOnClick: true,
            isFullWidthValue: true,
            value: '',
            valueKey: `erir_platform_id`,
        },
    ];

    const event: EventItem[] = [
        {
            itemType: 'text',
            label: 'Тип события',
            value: '',
            valueKey: 'event_type',
            template: (i: string) => translateEventType(i),
        },
        {
            itemType: 'date',
            label: 'Дата события',
            isShowTime: true,
            isSeconds: true,
            value: '',
            valueKey: 'event_date',
        },
        {
            itemType: 'date',
            label: 'Дата добавления в базу',
            isShowTime: true,
            isSeconds: true,
            value: '',
            valueKey: 'event_creation_date',
        },
        {
            itemType: 'text',
            label: 'Transaction ID',
            value: '',
            valueKey: 'transaction_id',
        },
        {
            itemType: 'text',
            label: 'Click ID',
            value: '',
            valueKey: 'click_id',
        },
        {
            itemType: 'text',
            label: 'User ID',
            value: '',
            valueKey: 'user_id',
        },
        {
            itemType: 'code',
            label: 'Данные события',
            isScrollable: true,
            isFormatJson: true,
            isColumnOnMobile: true,
            value: '',
            valueKey: 'event_data',
        },
    ];

    const request: EventItem[] = [
        {
            itemType: 'text',
            label: 'Метод запроса',
            value: '',
            valueKey: 'request_method',
        },
        {
            itemType: 'code',
            label: 'URL запроса',
            isFullWidthValue: true,
            isColumnOnMobile: true,
            value: '',
            valueKey: 'request_url',
        },
        {
            itemType: 'text',
            label: 'IP клиента',
            value: '',
            valueKey: 'client_ip',
        },
        {
            itemType: 'text',
            label: 'User-Agent',
            value: '',
            valueKey: 'user_agent',
            isClamp: false,
        },
        {
            itemType: 'code',
            label: 'Referer URL',
            isFullWidthValue: true,
            value: '',
            valueKey: `referer`,
        },
        {
            itemType: 'boolean',
            label: 'Валидность',
            valueKey: 'is_valid',
            value: '',
            trueText: 'Да',
            falseText: 'Нет',
            falseColor: 'red',
        },
        {
            itemType: 'code',
            label: 'Причини невалидности',
            isFullWidthValue: true,
            value: '',
            valueKey: 'invalid_reason',
            color: 'red',
        },
    ];

    const response: EventItem[] = [
        {
            itemType: 'text',
            label: 'Код ответа',
            value: '',
            valueKey: 'response_code',
        },
        {
            itemType: 'code',
            label: 'Redirect',
            isFullWidthValue: true,
            isColumnOnMobile: true,
            value: '',
            valueKey: 'response_location',
        },
        {
            itemType: 'boolean',
            label: 'Backurl',
            trueText: 'Да',
            valueKey: 'is_backurl',
            value: '',
            falseText: 'Нет',
        },
        {
            itemType: 'text',
            label: 'Backurl reason',
            value: '',
            valueKey: 'backurl_reason',
        },
    ];

    const cards: EventCards = {
        offer: { title: 'Оффер и рекламодатель', items: offer, col: 0 },
        affiliate: { title: 'Партнёр', items: affiliate, col: 0 },
        subIds: { title: 'Sub ID партнёра', items: subIds, col: 0 },
        erir: { title: 'Erir', items: erir, col: 0 },
        event: { title: 'Событие', items: event, col: 1 },
        request: { title: 'Запрос', items: request, col: 1 },
        response: { title: 'Ответ', items: response, col: 1 },
    };

    const prepareFields = (data: EventTrace) => {
        for (const card of Object.values(cards)) {
            card.items.forEach((item) => {
                const key = item.valueKey!;
                if (item.itemType === 'boolean') {
                    item.value = data[key] ?? '';
                } else {
                    item.value = `${data[key] ?? ''}`;
                }
            });
        }
    };

    const getCards = (data: EventTrace) => {
        prepareFields(data);
        for (const card of Object.values(cards)) {
            card.items = card.items.filter(item => item.value !== '');
        }
        return cards;
    };

    const getFields = () => {
        const fields: (keyof EventTrace)[] = ['is_backurl', 'offer_name', 'offer_status', 'affiliate_status', 'offer_url_status', 'master_name', 'buffer_account_name', 'advertiser_name', 'offer_goal_name', 'offer_url_id', 'offer_url_name'];
        for (const card of Object.values(cards)) {
            card.items.forEach((item) => {
                const key = item.valueKey! as keyof EventTrace;
                !fields.includes(key) && fields.push(key);
            });
        }
        return fields;
    };

    return { getCards, getFields };
};
